import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Menu, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

interface DropdownItem {
	label: string;
	onClick: () => void;
	icon?: React.ReactNode | IconDefinition;
	disabled?: boolean;
	danger?: boolean;
	title?: string;
}

const isIconDefinition = (value: unknown): value is IconDefinition =>
	typeof value === 'object' &&
	value != null &&
	('iconName' in value || 'prefix' in value);

interface DropdownProps {
	trigger: React.ReactNode;
	items: DropdownItem[];
	align?: 'left' | 'right';
	className?: string;
	/**
	 * Render the menu in a portal anchored to the trigger (Headless UI `anchor`)
	 * so it floats above the page rather than being clipped by an ancestor with
	 * `overflow: auto/hidden`. Use inside scrollable cards/sidebars. Defaults off
	 * (inline absolute positioning) to preserve existing call sites.
	 */
	floating?: boolean;
}

export function Dropdown({
	trigger,
	items,
	align = 'left',
	className,
	floating = false,
}: DropdownProps) {
	const colors = useThemeColors();
	return (
		<Menu as="div" className={cn('relative inline-block text-left', className)}>
			<Menu.Button as={Fragment}>{trigger}</Menu.Button>

			<Transition
				as={Fragment}
				enter="transition ease-out duration-100"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<Menu.Items
					anchor={
						floating
							? {
									to: align === 'right' ? 'bottom end' : 'bottom start',
									gap: 8,
								}
							: undefined
					}
					className={cn(
						'z-[100] min-w-56 rounded-md shadow-lg ring-1 ring-black ring-opacity-5',
						colors.classes.focusRingNone,
						colors.classes.bgPrimary,
						// When floating, the anchor handles positioning; otherwise fall
						// back to inline absolute positioning relative to the trigger.
						floating
							? ''
							: cn(
									'absolute mt-2 origin-top-right',
									align === 'right' ? 'right-0' : 'left-0',
								),
					)}
				>
					<div className="py-1">
						{items.map((item, index) => (
							<Menu.Item key={index} disabled={item.disabled}>
								{({ active }) => (
									<button
										onClick={item.onClick}
										title={item.title}
										className={cn(
											'group flex w-full items-center whitespace-nowrap px-4 py-2 text-sm transition-colors',
											item.disabled
												? 'opacity-50 cursor-not-allowed'
												: active
													? 'bg-subtle text-default'
													: '',
											!active &&
												(item.danger
													? colors.classes.textDanger
													: colors.classes.textPrimary),
										)}
										disabled={item.disabled}
									>
										{item.icon && (
											<Text
												className={cn(
													'mr-3',
													colors.classes.textSecondary,
													'group-hover:opacity-80',
												)}
											>
												{isIconDefinition(item.icon) ? (
													<Icon icon={item.icon} />
												) : (
													item.icon
												)}
											</Text>
										)}
										{item.label}
									</button>
								)}
							</Menu.Item>
						))}
					</div>
				</Menu.Items>
			</Transition>
		</Menu>
	);
}
